import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useLazyVerifyTinyTokenQuery, useVerifyTinyTokenQuery } from "./features/oidcSlice";
import { useCustomAuth } from "./app/auth/useCustomAuth";
import { profileApi, useUpdateProfileMutation } from "./features/profilesSlice";
import { useAuthProfile } from "./app/hooks";
import { Condition, coonditionContext } from ".";

import { useMessageAgentMutation } from "./features/adminSlice";
import { AppLayout } from "./App";
import { LoadingScreen } from "./LoadingScreen";
import { skipToken } from "@reduxjs/toolkit/query";
import { printError } from "./app/utils";
import type { Location } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { addTrace } from "./app/debugTrace";
import { setIsMagicLogin } from "./features/userTokenManagementSlice";

export const USER_STATE_ID = "user_state";

export const maskEmailKeepEnds = (email: string, maskChar: string = '*'): string => {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email; // invalid email fallback

  if (local.length <= 2) {
    // For very short local parts like "ab" or "a"
    return local[0] + maskChar.repeat(Math.max(1, local.length - 1)) + '@' + domain;
  }

  const maskedLocal =
    local[0] + maskChar.repeat(local.length - 2) + local[local.length - 1];
  return `${maskedLocal}@${domain}`;
};

export const MagicLinkGuard2: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  // *** TEST CODE: Immediately returning children to see if the router match works. ***
  //console.log("MagicLinkGuard Test: Immediately returning children.", children);
  return (<Outlet />);
}

export const getCleanedLocationUrl = (parm: string, location: Location) => {
  const cleanedParams = new URLSearchParams(location.search);
  cleanedParams.delete(parm);
  const cleanSearch = cleanedParams.toString();
  const cleanUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`;
  return cleanUrl;
}
export const MagicLinkGuard: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [ready, setReady] = useState(false);
  const [cleanURL, setCleanURL] = useState<string | undefined>(undefined);
  //const { isAuthenticated, user, login, logout, error } = useCustomAuth()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const t = params.get("t");


  const userSignIn = params.get("userSignIn") === 'true'
  const noLoginPrompt = params.get("nlp") === 'true'
  const phooneNumberToLink = params.get("phone");
  const conversationId = params.get("conversationId");

  //const { isLoading: isLoadingAuthInitializer, isSuccess: isSuccessAuthInitializer } = useAuthInitializer(location.pathname.includes('out') || !!t); // Initialize the auth flow if the user has logged out
  const { authStatus, profile: loggedInUserProfile, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile, error: errorLoggedInUserProfile, customAuth, auth, logoutFromAnyAuthMethod } = useAuthProfile()


  const [UpdateProfile, { isLoading: isUpdating, isSuccess, isError, error, data: updatedFormData/*, reset*/, originalArgs }] = useUpdateProfileMutation()
  const [messageAgent, { isLoading: isUpdatingMessageAgent, isSuccess: isSuccessMessageAgent, isError: isErrorMessageAgent, error: errorMessageAgent, data: messageAgentData/*, reset*/, originalArgs: originalArgsMessageAgent }] = useMessageAgentMutation()

  const { condition, baseName } = React.useContext<Condition>(coonditionContext);
  const isAuthenticated = authStatus === 'authenticated'
  // const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // const t = params.get("t");

  // const userSignIn = params.get("userSignIn") === 'true'
  // const noLoginPrompt = params.get("nlp") === 'true'
  // const phooneNumberToLink = params.get("phone");
  // const conversationId = params.get("conversationId");
  // //skipping useAuthInitializer for t
  //const { isLoading: isLoadingAuthInitializer, isSuccess: isSuccessAuthInitializer } = useAuthInitializer(location.pathname.includes('out') || !!t); // Initialize the auth flow if the user has logged out
  const VerifyTinyTokenQueryArg = (/*!userSignIn &&*/ t && !isAuthenticated && !customAuth.isAuthenticated) ? { t, signInMode: "exist" } : skipToken
  const { data: tokenData, isLoading: verifyTinyTokenIsLoading, isSuccess: verifyTinyTokenIsSuccess, isError: verifyTinyTokenIsError, error: verifyTinyTokenError } = useVerifyTinyTokenQuery(VerifyTinyTokenQueryArg);
  const cleanedRef = useRef(false);
  const getCleanedUrl = (parm: string) => {
    const cleanedParams = new URLSearchParams(location.search);
    // cleanedParams.delete(parm);
    // const cleanSearch = cleanedParams.toString();
    // const cleanUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`;
    const cleanUrl = getCleanedLocationUrl(parm, location)
    cleanedRef.current = true;
    return cleanUrl;
  }

  const [renderOutlet, setRenderOutlet] = useState(false);

  // TEMP DEBUG: append to shared trace, visible in long-press AuthProfileDialog.
  const appendDebug = React.useCallback((tag: string) => {
    const search = window.location.search || '(empty)';
    addTrace(`MLG:${tag}`, `search=${search}`);
  }, []);

  useEffect(() => {
    appendDebug(`effect-fire t=${t} convoId=${conversationId} authStatus=${authStatus} profileLoading=${isLoadingLoggedInUserProfile} cleaned=${cleanedRef.current}`);
    if (cleanedRef.current) {
      appendDebug('effect-return-cleanedRef');
      return;
    }
    const handleToken = async () => {
      if (/*!isLoadingAuthInitializer &&*/ !isLoadingLoggedInUserProfile && !isUpdating) {
        appendDebug(`profile-ready authed=${isAuthenticated} t-branch=${!!t}`);
        //console.log(`MagicLinkGuard auth finished loading.  isAuthenticated:`, isAuthenticated);
        if ((!userSignIn && phooneNumberToLink) || (auth?.user?.state as any)?.phooneNumberToLink) {
          //console.log("phooneNumberToLink", phooneNumberToLink, "(auth?.user?.state as any)?.phooneNumberToLink", (auth?.user?.state as any)?.phooneNumberToLink);
          if (isAuthenticated && isSuccessLoggedInUserProfile && !isUpdating) {
            try {
              if (loggedInUserProfile.phone !== phooneNumberToLink) {
                await UpdateProfile({ id: loggedInUserProfile.id, profileDTO: { communicate_by_messenger: true, phone: phooneNumberToLink ?? (auth?.user?.state as any)?.phooneNumberToLink } }).unwrap();
                dispatch(profileApi.util.invalidateTags([{ type: 'Profile', id: loggedInUserProfile.id }]));
              }
              //const messageAgentRet =  await
              messageAgent({
                originationNumber: phooneNumberToLink,
                messageBody: `the phone number ${loggedInUserProfile.phone !== phooneNumberToLink ? 'has been successfully linked to the account' : 'has been linked to the same account previously'} "`,//for ${loggedInUserProfile.name_public} `//${maskEmailKeepEnds(loggedInUserProfile.email)} account`
                systemMessageContent: { name: USER_STATE_ID, content: "ACCOUNT STATUS: This user now has a verified account." }
              })//.unwrap();
              //console.log("messageAgentRet:", messageAgentRet)
            } catch (err) {
              console.warn("phone link error:", err);
              var messageBody = `the phone number could be linked:  ${err} `
              if ((err as any)?.data?.error?.includes("duplicate key")) {
                messageBody = `The phone number could NOT be linked because it is already linked to a different account.`
              }
              const systemMessageContent = { content: "The phone number could NOT be linked because it is already linked to a different account.  If you have more than one Patient Connect logins, log in to the Patient connect wetsite with one of your other logins and try again" }//no name: USER_STATE_ID, so no messages get deleted by 
              messageAgent({ originationNumber: phooneNumberToLink, messageBody, systemMessageContent })//.unwrap();
            } finally {
              // Clean the phone param from the URL regardless of success/failure
              if ((auth?.user?.state as any)?.phooneNumberToLink) {
                (auth!.user!.state as any).phooneNumberToLink = undefined;
              }
              if ((auth?.user?.state as any)?.location) {
                const cleanedParams = new URLSearchParams((auth?.user?.state as any)?.location.search);
                cleanedParams.delete("phone");
                const cleanSearch = cleanedParams.toString();
                const cleanUrl = `${(auth?.user?.state as any)?.location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`;
                if ((auth?.user?.state as any)?.location) {
                  (auth!.user!.state as any).location = undefined;
                }
                setRenderOutlet(true)
                navigate(cleanUrl, { replace: true });
              }
              if (phooneNumberToLink) {
                const cleanedParams = new URLSearchParams(location.search);
                cleanedParams.delete("phone");
                const cleanSearch = cleanedParams.toString();
                const cleanUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`;
                setRenderOutlet(true)
                navigate(cleanUrl, { replace: true });
              }
              setRenderOutlet(true)
              return
            }
          }
          if (!isAuthenticated && !isUpdating) {
            //console.log("Not signed it while processing phooneNumberToLink", phooneNumberToLink, location.state?.phooneNumberToLink);
            if (conversationId) {
              setRenderOutlet(true)
              return
            } else
              auth.signinRedirect({
                state: { baseName, phooneNumberToLink, location },
              })
          }
        }

        if (t) {
          // Communicate "this session started with a magic-link token" to the
          // rest of the app via Redux (selectIsMagicLogin →
          // useAuthProfile().isMagicLogin). Stable flag — once set, doesn't
          // flip back when the URL is later cleaned.
          dispatch(setIsMagicLogin(true));
          if (isErrorLoggedInUserProfile) {
            const errStr = printError(errorLoggedInUserProfile)
            if (errStr.includes("Must log out")) {
              //await logoutFromAnyAuthMethod()
            }
            console.error(errStr)
            navigate(getCleanedUrl("t"), { replace: true });
            return
          }
          if (isSuccessLoggedInUserProfile) {
            if (isAuthenticated) {
              appendDebug(`already-authed convoId=${conversationId}`);
              if (conversationId) {
                setRenderOutlet(true)
                return
              }
              navigate(getCleanedUrl("t"), { replace: true });
              return
            }

            if (verifyTinyTokenIsSuccess) {
              appendDebug('token-verified-start');
              //console.log("Magic token verified, cookies set");
              const { sub, profile, expires_at } = tokenData;
              await auth.removeUser() // so we are not confused crear OIDC cache
              appendDebug('after-removeUser');
              customAuth.login(tokenData)//, "")//will not navigate
              appendDebug('after-customAuthLogin');
              // Remove the token from URL after successful login
              setRenderOutlet(true)
              const cleaned = getCleanedUrl("t");
              appendDebug(`navigate-to=${cleaned}`);
              navigate(cleaned, { replace: true });
              appendDebug('after-navigate');
              return
            }

            if (verifyTinyTokenIsError) {
              if ((verifyTinyTokenError as any)?.data?.isExpired) {
                console.warn("Magic token expired")
              } else if ((verifyTinyTokenError as any)?.data?.isUknownUser) {
                console.warn("No existing cognito user") //letting outlet deal with auth          
              } else if (verifyTinyTokenIsError) {
                const errStr = printError(verifyTinyTokenError)
                console.warn("Magic token error", errStr);
                // const redirectParam = encodeURIComponent(getCleanedUrl("t")); // should really be saved to state; need to figure out how, where and who
                // navigate(`/SignIn?redirect=${redirectParam}`, { replace: true });
                // return //return <Outlet/>
              }
              const { pathname, search, hash } = location
              if (conversationId || pathname.split('/').includes('conversations') || noLoginPrompt) {
                setRenderOutlet(true)
                return
              }
              const currentRelativeUrl = `${pathname}${search}${hash}`;
              const redirectParam = getCleanedUrl("t");
              navigate(`/SignIn?t=${t}&redirect=${encodeURIComponent(redirectParam)}`, { replace: true });
              return
            }
          }
        }
      }
    };

    handleToken();
  }, [location, navigate, loggedInUserProfile, authStatus, isLoadingLoggedInUserProfile, isSuccessLoggedInUserProfile, //isLoadingAuthInitializer,
    verifyTinyTokenIsSuccess, verifyTinyTokenIsError
  ]);

  const isHealthWalletPath = window.location.pathname.includes('/healthwallet');
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  if (isHealthWalletPath && code) {
    console.log('Health wallet callback', code, state);
    return (<LoadingScreen secondLine="Health Wallet" />)
  }
  if (renderOutlet || (!t && !phooneNumberToLink && !(auth?.user?.state as any)?.phooneNumberToLink)) {
    return (<Outlet />);
  }
  return (<LoadingScreen />)
};
